<template>
  <div>
    <el-alert v-if="generalError" :title="generalError.title" :description="generalError.description" type="error" show-icon style="margin-bottom: 12px;" :closable="false"/>
    <el-alert v-if="error" :title="error.title" :description="error.description" type="error" show-icon style="margin-bottom: 12px;" :closable="false"/>
    <el-alert v-if="rateLimitMessage" type="warning" show-icon style="margin-bottom: 12px;" :closable="false">
      <template #title>
        Veuillez patienter
      </template>
      <template #default>
        {{ rateLimitMessage }}
      </template>
    </el-alert>
    <h2 class="signup-form-title">{{$t('signup.phone.title')}}</h2>
    <div class="form-group">
      <p class="signup-form-description">{{$t('signup.phone.description')}}</p>
    </div>

    <el-form-item :label="$t('signup.phone.label')">
      <VueTelInput v-model="edit.phone" :inputOptions="inputOptions" mode="international" :default-country="'FR'" @country-changed="saveCountry"></VueTelInput>
    </el-form-item>
    <p class="condition-text"><Lock class="text-icon"/>{{$t('signup.phone.privacy')}}</p>
    
    <!-- Widget Turnstile avec fallback visible -->
    <div id="turnstile-widget-sms" style="display: none;"></div>
    <div id="turnstile-widget-fallback" style="display: none;">
      <div class="turnstile-fallback-container">
        <p class="text-warning mb-2">
          <i class="fas fa-exclamation-triangle"></i>
          Problème de connexion avec la vérification de sécurité
        </p>
                 <div id="turnstile-visible-widget"></div>
         <p class="text-muted small mt-2">
           Veuillez compléter cette vérification pour continuer
         </p>
         <button type="button" class="btn btn-sm btn-outline-primary" @click="retryTurnstile">
           <i class="fas fa-redo"></i> Réessayer
         </button>
      </div>
    </div>
    
          <div class="form-group mt-4">
        <el-button class="signup-primary" :class="canSubmit ? '' : 'disabled'" @click="continu" :loading="isLoading" :disabled="!canSubmit">
          {{$t('signup.continue')}}
        </el-button>
        <p v-if="!turnstileInitialized" class="text-muted small mt-2">
          <i class="fas fa-spinner fa-spin"></i> Chargement de la vérification de sécurité...
        </p>
      </div>
  </div>
</template>

<script>
import { VueTelInput } from 'vue-tel-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import signupHandler from "../mixins/signupHandler.js";
import 'vue-tel-input/vue-tel-input.css';

export default {
  name: "RegisterPhoneNumber",
  mixins: [signupHandler],
  data () {
    return {
      inputOptions: {
        placeholder: this.$t('signup.phone.placeholder')
      },
      edit: {
        phone: '',
        country: ''
      },
      turnstileToken: null,
      turnstileWidgetId: null,
      turnstileFallbackWidgetId: null,
      turnstilePromise: null,
      turnstileResolve: null,
      turnstileReject: null,
      turnstileMode: 'invisible', // 'invisible' ou 'visible'
      turnstileInitialized: false,
      turnstileError: false,
      rateLimitMinutes: 3, // Délai en minutes entre chaque envoi
      rateLimitTimer: null,
      timeRemaining: 0
    }
  },
  created() {
    let user = JSON.parse(JSON.stringify(this.signupUser))
    if (user.telephone) {
      this.edit.phone = user.telephone.telephone
    }
    // Vérifier le rate limit au chargement
    this.checkRateLimit();
  },
  mounted() {
    this.initTurnstile();
    // Démarrer le timer si nécessaire
    if (this.isRateLimited) {
      this.startRateLimitTimer();
    }
  },
  beforeUnmount() {
    // Nettoyer les widgets Turnstile
    if (this.turnstileWidgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (e) {
        console.warn('Erreur lors du nettoyage du widget invisible:', e);
      }
    }
    if (this.turnstileFallbackWidgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.turnstileFallbackWidgetId);
      } catch (e) {
        console.warn('Erreur lors du nettoyage du widget visible:', e);
      }
    }
    // Nettoyer le timer
    if (this.rateLimitTimer) {
      clearInterval(this.rateLimitTimer);
    }
  },
  computed: {
    submitUrl () {
      return 'user/send-sms-code'
    },
    parsePhoneNumber () {
      if (this.edit.phone.length < 5) {
        return parsePhoneNumberFromString(this.edit.phone)
      } else {
        return false
      }
    },
    hasChanged () {
      // return this.edit.phone.length > 5
      const phoneNumber = parsePhoneNumberFromString(this.edit.phone.replaceAll(' ', ''))
      return typeof phoneNumber !== 'undefined' && phoneNumber.isValid()
    },
    canSubmit() {
      return this.hasChanged && !this.isRateLimited && this.turnstileInitialized && !this.turnstileError;
    },
    isRateLimited() {
      return this.timeRemaining > 0;
    },
    rateLimitMessage() {
      if (!this.isRateLimited) return null;
      
      const minutes = Math.floor(this.timeRemaining / 60);
      const seconds = this.timeRemaining % 60;
      
      if (minutes > 0) {
        return `Vous pourrez renvoyer un code dans ${minutes} minute${minutes > 1 ? 's' : ''} et ${seconds} seconde${seconds > 1 ? 's' : ''}`;
      } else {
        return `Vous pourrez renvoyer un code dans ${seconds} seconde${seconds > 1 ? 's' : ''}`;
      }
    }
  },
  methods: {
    initTurnstile() {
      // Vérifier si Turnstile est déjà chargé
      if (window.turnstile) {
        this.renderTurnstile();
      } else {
        // Charger le script Turnstile
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.renderTurnstile();
        };
        document.head.appendChild(script);
      }
    },
    
    renderTurnstile() {
      const siteKey = this.getTurnstileSiteKey();
      if (!siteKey) {
        this.showTurnstileError('Clé Turnstile non configurée');
        return;
      }
      
      console.log('Utilisation de la clé Turnstile:', siteKey.substring(0, 10) + '...');
      
      // Essayer d'abord le mode invisible
      this.renderInvisibleTurnstile(siteKey);
    },
    
    getTurnstileSiteKey() {
      // 1. Variable globale window
      if (window.TURNSTILE_SITE_KEY && window.TURNSTILE_SITE_KEY !== '') {
        return window.TURNSTILE_SITE_KEY;
      }
      // 2. Depuis symfony global si disponible
      else if (window.symfony && window.symfony.turnstile_site_key) {
        return window.symfony.turnstile_site_key;
      }
      // 3. Depuis this.$symfony si disponible
      else if (this.$symfony && this.$symfony.turnstile_site_key) {
        return this.$symfony.turnstile_site_key;
      }
      // 4. Chercher dans le DOM (peut-être stocké dans un meta tag)
      else {
        const metaTag = document.querySelector('meta[name="turnstile-site-key"]');
        if (metaTag) {
          return metaTag.getAttribute('content');
        }
      }
      
      // 5. En dernier recours, utiliser la clé en dur (à remplacer par votre vraie clé)
      console.warn('Aucune clé Turnstile configurée - Utilisation de la clé par défaut');
      return '0x4AAAAAAAVnYgGoBbrMTyZW'; // À REMPLACER
    },
    
    renderInvisibleTurnstile(siteKey) {
      const container = document.getElementById('turnstile-widget-sms');
      if (!container) {
        console.error('Conteneur Turnstile introuvable');
        this.showVisibleFallback(siteKey);
        return;
      }
      
      try {
        this.turnstileWidgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          callback: (token) => {
            this.onTurnstileSuccess(token);
          },
          size: 'invisible',
          'error-callback': (error) => {
            console.error('Erreur Turnstile invisible:', error);
            this.showVisibleFallback(siteKey);
          },
          'expired-callback': () => {
            console.warn('Token Turnstile expiré');
            this.turnstileToken = null;
          },
          'timeout-callback': () => {
            console.warn('Timeout Turnstile');
            this.showVisibleFallback(siteKey);
          }
        });
        
        this.turnstileInitialized = true;
        this.turnstileMode = 'invisible';
        console.log('Widget Turnstile invisible créé avec ID:', this.turnstileWidgetId);
        
      } catch (error) {
        console.error('Erreur lors de la création du widget invisible:', error);
        this.showVisibleFallback(siteKey);
      }
    },
    
    showVisibleFallback(siteKey) {
      console.log('Activation du fallback visible Turnstile');
      this.turnstileMode = 'visible';
      
      // Cacher le conteneur invisible et montrer le fallback
      const invisibleContainer = document.getElementById('turnstile-widget-sms');
      const fallbackContainer = document.getElementById('turnstile-widget-fallback');
      
      if (invisibleContainer) {
        invisibleContainer.style.display = 'none';
      }
      if (fallbackContainer) {
        fallbackContainer.style.display = 'block';
      }
      
      // Render le widget visible
      const visibleContainer = document.getElementById('turnstile-visible-widget');
      if (!visibleContainer) {
        this.showTurnstileError('Conteneur fallback Turnstile introuvable');
        return;
      }
      
      try {
        this.turnstileFallbackWidgetId = window.turnstile.render(visibleContainer, {
          sitekey: siteKey,
          callback: (token) => {
            this.onTurnstileSuccess(token);
          },
          size: 'normal',
          theme: 'light',
          'error-callback': (error) => {
            console.error('Erreur Turnstile visible:', error);
            this.showTurnstileError('Erreur lors de la vérification de sécurité: ' + error);
          },
          'expired-callback': () => {
            console.warn('Token Turnstile visible expiré');
            this.turnstileToken = null;
          }
        });
        
        this.turnstileInitialized = true;
        console.log('Widget Turnstile visible créé avec ID:', this.turnstileFallbackWidgetId);
        
      } catch (error) {
        console.error('Erreur lors de la création du widget visible:', error);
        this.showTurnstileError('Impossible de charger la vérification de sécurité');
      }
    },
    
    onTurnstileSuccess(token) {
      this.turnstileToken = token;
      this.turnstileError = false;
      console.log('Token Turnstile SMS reçu');
      
      // Résoudre la promesse si elle existe
      if (this.turnstileResolve) {
        this.turnstileResolve(token);
        this.turnstileResolve = null;
        this.turnstileReject = null;
      }
    },
    
    showTurnstileError(errorMessage) {
      this.turnstileError = true;
      this.error = {
        title: 'Problème de sécurité',
        description: errorMessage
      };
      console.error('Erreur Turnstile:', errorMessage);
    },
    
    saveCountry ({iso2}) {
      this.edit.country = iso2
    },
    
    async executeTurnstileAndSend() {
      // Vérifier que Turnstile est initialisé
      if (!this.turnstileInitialized) {
        throw new Error('Widget Turnstile non initialisé');
      }
      
      // Si on a déjà un token valide, le retourner
      if (this.turnstileToken) {
        console.log('Token Turnstile déjà disponible');
        return this.turnstileToken;
      }
      
      // Si on est en mode visible, attendre que l'utilisateur complète le captcha
      if (this.turnstileMode === 'visible') {
        return this.waitForVisibleTurnstile();
      }
      
      // Mode invisible : exécuter automatiquement
      return this.executeInvisibleTurnstile();
    },
    
    async executeInvisibleTurnstile() {
      // Réinitialiser le token
      this.turnstileToken = null;
      
      // Créer une promesse qui sera résolue par le callback du widget
      this.turnstilePromise = new Promise((resolve, reject) => {
        this.turnstileResolve = resolve;
        this.turnstileReject = reject;
        
        // Timeout après 15 secondes
        setTimeout(() => {
          if (!this.turnstileToken) {
            console.log('Timeout Turnstile invisible - Passage en mode visible');
            this.showVisibleFallback(this.getTurnstileSiteKey());
            // Ne pas rejeter, juste passer en mode visible
          }
        }, 15000);
      });
      
      // Exécuter le challenge invisible
      try {
        console.log('Exécution de Turnstile invisible...');
        if (this.turnstileWidgetId) {
          window.turnstile.execute(this.turnstileWidgetId);
        } else {
          throw new Error('Widget ID non disponible');
        }
      } catch (error) {
        console.error('Erreur lors de l\'exécution de Turnstile invisible:', error);
        this.showVisibleFallback(this.getTurnstileSiteKey());
        return this.waitForVisibleTurnstile();
      }
      
      // Attendre que le callback soit appelé
      return this.turnstilePromise;
    },
    
    async waitForVisibleTurnstile() {
      // Pour le mode visible, on attend que l'utilisateur complète le captcha
      if (this.turnstileToken) {
        return this.turnstileToken;
      }
      
      return new Promise((resolve, reject) => {
        this.turnstileResolve = resolve;
        this.turnstileReject = reject;
        
        // Timeout plus long pour le mode visible (60 secondes)
        setTimeout(() => {
          if (!this.turnstileToken) {
            reject(new Error('Veuillez compléter la vérification de sécurité'));
          }
        }, 60000);
      });
    },
    
    async beforeContinu () {
      // Vérifier le rate limit
      if (this.isRateLimited) {
        this.error = {
          title: 'Veuillez patienter',
          description: this.rateLimitMessage
        };
        return false;
      }
      
      try {
        // Exécuter Turnstile et attendre le token
        await this.executeTurnstileAndSend();
        
        // Ajouter le token aux données envoyées
        const dataToSend = {
          ...this.edit,
          'cf-turnstile-response': this.turnstileToken
        };
        
        const response = await this.sendData(dataToSend, 'phone')
        
        if (response && response.response && response.response.data && response.response.data.form_errors) {
          this.formErrors = response.response.data.form_errors
          const errors = Object.keys(this.formErrors)
          return false
        }
        
        // Si la requête est valide, activer le rate limit
        if (response && response.response && response.response.data && response.response.data.valid) {
          this.setRateLimit();
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Erreur Turnstile:', error);
        
        // Proposer des solutions selon le type d'erreur
        let errorDescription = 'Impossible de valider le captcha. ';
        if (error.message.includes('Timeout')) {
          errorDescription += 'Veuillez vérifier votre connexion internet et réessayer.';
        } else if (error.message.includes('non initialisé')) {
          errorDescription += 'Veuillez recharger la page.';
        } else if (error.message.includes('compléter la vérification')) {
          errorDescription += 'Veuillez compléter la vérification de sécurité ci-dessus.';
        } else {
          errorDescription += 'Utilisez le bouton "Réessayer" si le problème persiste.';
        }
        
        this.error = {
          title: 'Erreur de sécurité',
          description: errorDescription
        };
        return false;
      }
    },
    checkRateLimit() {
      const lastSentKey = 'lightyshare_sms_last_sent';
      const phoneKey = 'lightyshare_sms_phone';
      
      const lastSent = localStorage.getItem(lastSentKey);
      const lastPhone = localStorage.getItem(phoneKey);
      
      if (lastSent) {
        const now = Date.now();
        const timePassed = now - parseInt(lastSent);
        const timeLimit = this.rateLimitMinutes * 60 * 1000; // Convertir en millisecondes
        
        // Si c'est le même numéro ou si le délai n'est pas écoulé
        if (timePassed < timeLimit) {
          // Calculer le temps restant en secondes
          this.timeRemaining = Math.ceil((timeLimit - timePassed) / 1000);
        } else {
          // Le délai est écoulé, on peut nettoyer
          localStorage.removeItem(lastSentKey);
          localStorage.removeItem(phoneKey);
          this.timeRemaining = 0;
        }
      }
    },
    
    startRateLimitTimer() {
      // Nettoyer tout timer existant
      if (this.rateLimitTimer) {
        clearInterval(this.rateLimitTimer);
      }
      
      // Décrémenter chaque seconde
      this.rateLimitTimer = setInterval(() => {
        this.timeRemaining--;
        if (this.timeRemaining <= 0) {
          clearInterval(this.rateLimitTimer);
          this.rateLimitTimer = null;
          // Nettoyer localStorage
          localStorage.removeItem('lightyshare_sms_last_sent');
          localStorage.removeItem('lightyshare_sms_phone');
        }
      }, 1000);
    },
    
    setRateLimit() {
      const now = Date.now();
      localStorage.setItem('lightyshare_sms_last_sent', now.toString());
      localStorage.setItem('lightyshare_sms_phone', this.edit.phone);
      
      // Définir le temps restant
      this.timeRemaining = this.rateLimitMinutes * 60; // En secondes
      
      // Démarrer le timer
      this.startRateLimitTimer();
    },
    
    retryTurnstile() {
      console.log('Retry Turnstile demandé');
      
      // Nettoyer l'état actuel
      this.turnstileToken = null;
      this.turnstileError = false;
      this.turnstileInitialized = false;
      this.error = null;
      
      // Nettoyer les widgets existants
      if (this.turnstileWidgetId && window.turnstile) {
        try {
          window.turnstile.remove(this.turnstileWidgetId);
        } catch (e) {
          console.warn('Erreur lors du nettoyage du widget invisible:', e);
        }
        this.turnstileWidgetId = null;
      }
      
      if (this.turnstileFallbackWidgetId && window.turnstile) {
        try {
          window.turnstile.remove(this.turnstileFallbackWidgetId);
        } catch (e) {
          console.warn('Erreur lors du nettoyage du widget visible:', e);
        }
        this.turnstileFallbackWidgetId = null;
      }
      
      // Cacher les conteneurs
      const invisibleContainer = document.getElementById('turnstile-widget-sms');
      const fallbackContainer = document.getElementById('turnstile-widget-fallback');
      
      if (invisibleContainer) {
        invisibleContainer.style.display = 'none';
      }
      if (fallbackContainer) {
        fallbackContainer.style.display = 'none';
      }
      
      // Réinitialiser Turnstile
      setTimeout(() => {
        this.initTurnstile();
      }, 500);
    },
  },
  components: {
    VueTelInput
  },
}
</script>

<style scoped>
.turnstile-fallback-container {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin: 16px 0;
  text-align: center;
}

.turnstile-fallback-container .text-warning {
  color: #856404 !important;
  font-weight: 500;
}

.turnstile-fallback-container .fas {
  margin-right: 8px;
}

.turnstile-fallback-container .text-muted {
  color: #6c757d !important;
  font-size: 0.875rem;
}

#turnstile-visible-widget {
  display: flex;
  justify-content: center;
  margin: 16px 0;
}

/* Style pour les alertes d'erreur Turnstile */
.el-alert.el-alert--error {
  margin-bottom: 16px;
}
</style>